import { useEffect, useRef, useState, type ComponentType } from "react";
import {
  Ambulance,
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpFromDot,
  Banana,
  CarFront,
  Cctv,
  CircleAlert,
  CircleCheck,
  CircleSlash2,
  CloudFog,
  CloudRain,
  CloudRainWind,
  Construction,
  CornerUpLeft,
  CornerUpRight,
  FlagTriangleRight,
  Hand,
  HandHeart,
  HatGlasses,
  LifeBuoy,
  MapPinned,
  MessageCircle,
  OctagonAlert,
  PhoneCall,
  Road,
  Send,
  ShieldAlert,
  Snowflake,
  ThermometerSnowflake,
  Timer,
  TrafficCone,
  TriangleAlert,
  Turtle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

type ReportType =
  | "traffic"
  | "police"
  | "crash"
  | "hazard"
  | "closure"
  | "blocked_lane"
  | "map_issue"
  | "bad_weather"
  | "gas_prices"
  | "roadside_help"
  | "place";

type ReportFlow = "standard" | "closure" | "map_issue" | "roadside_help";

type ReportOption = {
  type: ReportType;
  label: string;
  icon: ComponentType<{ className?: string }>;
  tone: string;
  flow?: ReportFlow;
  details: ReportDetail[];
};

type ReportDetail = {
  type: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  general?: boolean;
};

const REPORT_OPTIONS: ReportOption[] = [
  {
    type: "hazard",
    label: "Hazard",
    icon: TriangleAlert,
    tone: "bg-yellow-500/18 text-yellow-200 ring-yellow-300/30",
    details: [
      { type: "hazard", label: "Hazard", icon: TriangleAlert, general: true },
      { type: "construction", label: "Construction", icon: TrafficCone },
      { type: "car_on_shoulder", label: "Car on shoulder", icon: CarFront },
      { type: "broken_traffic_light", label: "Broken traffic light", icon: CircleSlash2 },
      { type: "pothole", label: "Pothole", icon: CircleAlert },
      { type: "object", label: "Object", icon: Banana },
    ],
  },
  {
    type: "blocked_lane",
    label: "Blocked lane",
    icon: Construction,
    tone: "bg-orange-500/18 text-orange-200 ring-orange-300/30",
    details: [
      { type: "blocked_lane", label: "Blocked lane", icon: Construction, general: true },
      { type: "left_lane", label: "Left lane", icon: CornerUpLeft },
      { type: "center_lane", label: "Center lane", icon: ArrowUpFromDot },
      { type: "right_lane", label: "Right lane", icon: CornerUpRight },
    ],
  },
  {
    type: "crash",
    label: "Crash",
    icon: OctagonAlert,
    tone: "bg-red-500/18 text-red-300 ring-red-400/30",
    details: [
      { type: "crash", label: "Crash", icon: OctagonAlert, general: true },
      { type: "minor", label: "Minor", icon: CarFront },
      { type: "major", label: "Major", icon: Ambulance },
      { type: "other_side", label: "Other side", icon: ArrowDownLeft },
    ],
  },
  {
    type: "traffic",
    label: "Traffic",
    icon: CarFront,
    tone: "bg-green-500/18 text-green-300 ring-green-400/30",
    details: [
      { type: "traffic", label: "Traffic", icon: CarFront, general: true },
      { type: "heavy", label: "Heavy", icon: Turtle },
      { type: "standstill", label: "Standstill", icon: Hand },
    ],
  },
  {
    type: "police",
    label: "Police",
    icon: ShieldAlert,
    tone: "bg-sky-500/18 text-sky-300 ring-sky-400/30",
    details: [
      { type: "police", label: "Police", icon: ShieldAlert, general: true },
      { type: "mobile_camera", label: "Traffic camera", icon: Cctv },
      { type: "hidden", label: "Hidden", icon: HatGlasses },
      { type: "other_side", label: "Other side", icon: ArrowDownLeft },
    ],
  },
  {
    type: "map_issue",
    label: "Map issue",
    icon: MapPinned,
    tone: "bg-slate-500/18 text-slate-200 ring-slate-300/30",
    flow: "map_issue",
    details: [
      { type: "map_issue", label: "Map issue", icon: MapPinned, general: true },
    ],
  },
  {
    type: "bad_weather",
    label: "Bad weather",
    icon: CloudRainWind,
    tone: "bg-cyan-500/18 text-cyan-200 ring-cyan-300/30",
    details: [
      { type: "bad_weather", label: "Bad weather", icon: CloudRainWind, general: true },
      { type: "flood", label: "Flood", icon: CloudRain },
      { type: "fog", label: "Fog", icon: CloudFog },
      { type: "slippery_road", label: "Slippery road", icon: Road },
      { type: "icy_road", label: "Icy road", icon: ThermometerSnowflake },
      { type: "unplowed_road", label: "Unplowed road", icon: Snowflake },
    ],
  },
  {
    type: "roadside_help",
    label: "Roadside help",
    icon: LifeBuoy,
    tone: "bg-red-500/18 text-red-200 ring-red-300/30",
    flow: "roadside_help",
    details: [
      { type: "fellow_wazers", label: "Fellow Tazers", icon: HandHeart, general: true },
      { type: "emergency_call", label: "Emergency call", icon: PhoneCall },
      { type: "road_assistance", label: "Road assistance", icon: FlagTriangleRight },
    ],
  },
];

const AUTO_SUBMIT_SECONDS = 10;

export function ReportSheet() {
  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<ReportType | null>(null);
  const [selectedDetailType, setSelectedDetailType] = useState<string | null>(null);
  const selected = REPORT_OPTIONS.find((option) => option.type === selectedType);
  const selectedDetail =
    selected?.details.find((detail) => detail.type === selectedDetailType) ??
    selected?.details[0] ??
    null;
  const latestReportRef = useRef<{
    option: ReportOption | null;
    detail: ReportDetail | null;
  }>({ option: null, detail: null });
  const hasStandardAutoSubmit = selected?.flow === undefined;
  const autoSubmitActive = Boolean(
    open &&
      selected &&
      selectedDetail &&
      hasStandardAutoSubmit,
  );

  latestReportRef.current = {
    option: selected ?? null,
    detail: selectedDetail,
  };

  useEffect(() => {
    if (!autoSubmitActive) {
      return;
    }

    const handle = window.setTimeout(() => {
      const { detail, option } = latestReportRef.current;
      reportToApiPlaceholder(option, detail);
      setOpen(false);
      setSelectedType(null);
      setSelectedDetailType(null);
    }, AUTO_SUBMIT_SECONDS * 1_000);

    return () => window.clearTimeout(handle);
  }, [autoSubmitActive, selectedType]);

  const resetFlow = () => {
    setSelectedType(null);
    setSelectedDetailType(null);
  };

  const closeReport = () => {
    setOpen(false);
    resetFlow();
  };

  const openDetail = (option: ReportOption) => {
    setSelectedType(option.type);
    setSelectedDetailType(option.details[0]?.type ?? null);
  };

  const submitSelected = () => {
    reportToApiPlaceholder(selected ?? null, selectedDetail);
    closeReport();
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) resetFlow();
      }}
    >
      <SheetTrigger asChild>
        <Button
          variant="glass"
          size="icon"
          className="border-orange-400/80 text-orange-300 shadow-orange-500/20 hover:bg-orange-500/15 hover:text-orange-200"
          title="Report"
        >
          <TriangleAlert className="size-6" />
          <span className="sr-only">Report</span>
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className={cn(
          "min-h-[425px] max-h-[86vh] overflow-y-auto bg-popover text-popover-foreground",
          !selected && "[&>button:last-child]:hidden",
        )}
      >
        <style>
          {`
            @keyframes report-progress {
              from { transform: scaleX(0); }
              to { transform: scaleX(1); }
            }
          `}
        </style>
        {!selected ? (
          <ReportTypeGrid onSelect={openDetail} />
        ) : (
          <ReportDetailScreen
            selected={selected}
            selectedDetail={selectedDetail}
            onBack={resetFlow}
            onCancel={closeReport}
            onDetailSelect={(detail) => {
              setSelectedDetailType(detail.type);
            }}
            onSubmit={submitSelected}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function reportToApiPlaceholder(
  option: ReportOption | null,
  detail: ReportDetail | null,
) {
  // TODO: replace with the captured Waze mobile reporting endpoint + payload.
  console.info("report placeholder", {
    reportType: option?.type,
    reportLabel: option?.label,
    detailType: detail?.type,
    detailLabel: detail?.label,
  });
}

function ReportTypeGrid({
  onSelect,
}: {
  onSelect: (option: ReportOption) => void;
}) {
  return (
    <div className="mb-8 mt-8 grid grid-cols-4 gap-x-4 gap-y-8">
      {REPORT_OPTIONS.map((option) => {
        const Icon = option.icon;

        return (
          <button
            key={option.type}
            type="button"
            onClick={() => onSelect(option)}
            className="group flex min-h-0 min-w-0 flex-col items-center gap-4 rounded-2xl p-3 text-center transition active:scale-95"
          >
            <span
              className={cn(
                "flex size-20 items-center justify-center rounded-full ring-1 transition group-hover:scale-105",
                option.tone,
              )}
            >
              <Icon className="size-11" />
            </span>
            <span className="text-base font-semibold leading-tight">
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ReportDetailScreen({
  selected,
  selectedDetail,
  onBack,
  onCancel,
  onDetailSelect,
  onSubmit,
}: {
  selected: ReportOption;
  selectedDetail: ReportDetail | null;
  onBack: () => void;
  onCancel: () => void;
  onDetailSelect: (detail: ReportDetail) => void;
  onSubmit: () => void;
}) {
  if (selected.flow === "closure") {
    return (
      <SpecialReportScreen
        icon={Construction}
        title="Closure"
        description="Tap an arrow on the map to mark a road closure. Others will be routed around it."
        onBack={onBack}
        onCancel={onCancel}
      />
    );
  }

  if (selected.flow === "map_issue") {
    return (
      <SpecialReportScreen
        icon={MapPinned}
        title="Map issue"
        description="Map issue reports need a comment before they can be sent to Waze map editors."
        actionLabel="Add a comment"
        actionIcon={MessageCircle}
        onBack={onBack}
        onCancel={onCancel}
      />
    );
  }

  if (selected.flow === "roadside_help") {
    return (
      <RoadsideHelpScreen
        selected={selected}
        selectedDetail={selectedDetail}
        onBack={onBack}
        onCancel={onCancel}
        onDetailSelect={onDetailSelect}
        onSubmit={onSubmit}
      />
    );
  }

  return (
    <div className="pt-10">
      <button
        type="button"
        onClick={onBack}
        className="absolute left-4 top-4 inline-flex size-12 items-center justify-center rounded-full text-muted-foreground"
        title="Back to report types"
      >
        <ArrowLeft className="size-6" />
      </button>
      <ReportDetailGrid
        details={selected.details}
        selectedDetail={selectedDetail}
        onDetailSelect={onDetailSelect}
      />

      <div className="mt-14 flex gap-3">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="flex-1 rounded-full bg-secondary text-primary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="lg"
          className="relative flex-1 overflow-hidden rounded-full"
          onClick={onSubmit}
        >
          {selectedDetail && (
            <span
              key={selected.type}
              className="absolute inset-y-0 left-0 w-full origin-left bg-primary-foreground/25"
              style={{
                animation: `report-progress ${AUTO_SUBMIT_SECONDS}s linear forwards`,
              }}
            />
          )}
          <span className="relative inline-flex items-center gap-2">
            {selectedDetail?.general ? (
              <Timer className="size-5" />
            ) : (
              <Send className="size-5" />
            )}
            Report
          </span>
        </Button>
      </div>

      <p className="mt-4 text-center text-sm text-muted-foreground">
        Reporting automatically unless you cancel or choose Report now.
      </p>
    </div>
  );
}

function ReportDetailGrid({
  details,
  selectedDetail,
  onDetailSelect,
}: {
  details: ReportDetail[];
  selectedDetail: ReportDetail | null;
  onDetailSelect: (detail: ReportDetail) => void;
}) {
  const columnClass = details.length === 4 ? "grid-cols-4" : "grid-cols-3";

  return (
    <div className={cn("mt-10 grid gap-x-4 gap-y-10", columnClass)}>
      {details.map((detail) => {
        const Icon = detail.icon;
        const isSelected = selectedDetail?.type === detail.type;

        return (
          <button
            key={detail.type}
            type="button"
            onClick={() => onDetailSelect(detail)}
            className="flex min-h-0 min-w-0 flex-col items-center gap-3 rounded-2xl p-1 text-center transition active:scale-95"
            aria-pressed={isSelected}
          >
            <span
              className={cn(
                "relative flex size-20 items-center justify-center rounded-full bg-secondary text-secondary-foreground ring-2 transition",
                isSelected ? "ring-primary" : "ring-transparent",
              )}
            >
              <Icon className="size-11" />
              {isSelected && (
                <span className="absolute right-1 top-1 flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <CircleCheck className="size-4" />
                </span>
              )}
            </span>
            <span className="text-base font-semibold leading-tight">
              {detail.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SpecialReportScreen({
  actionIcon: ActionIcon,
  actionLabel,
  description,
  icon: Icon,
  onCancel,
  onBack,
  title,
}: {
  actionIcon?: ComponentType<{ className?: string }>;
  actionLabel?: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  onCancel: () => void;
  onBack: () => void;
  title: string;
}) {
  return (
    <div className="pt-10 text-center">
      <button
        type="button"
        onClick={onBack}
        className="absolute left-4 top-4 inline-flex size-12 items-center justify-center rounded-full text-muted-foreground"
        title="Back to report types"
      >
        <ArrowLeft className="size-6" />
      </button>
      <div className="mx-auto mt-10 flex size-24 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
        <Icon className="size-12" />
      </div>
      <h2 className="mt-5 text-3xl font-bold">{title}</h2>
      <div className="my-8 border-t border-border" />
      {ActionIcon && actionLabel && (
        <button
          type="button"
          className="mx-auto mb-8 flex items-center gap-4 rounded-2xl px-5 py-4 text-lg text-muted-foreground"
        >
          <ActionIcon className="size-8 text-foreground" />
          {actionLabel}
        </button>
      )}
      <p className="mx-auto max-w-sm text-balance text-base font-medium text-muted-foreground">
        {description}
      </p>
      <div className="mt-10 flex gap-3">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="flex-1 rounded-full bg-secondary text-primary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="lg"
          className="flex-1 rounded-full"
          disabled
        >
          Send
        </Button>
      </div>
    </div>
  );
}

function RoadsideHelpScreen({
  selected,
  selectedDetail,
  onBack,
  onCancel,
  onDetailSelect,
  onSubmit,
}: {
  selected: ReportOption;
  selectedDetail: ReportDetail | null;
  onBack: () => void;
  onCancel: () => void;
  onDetailSelect: (detail: ReportDetail) => void;
  onSubmit: () => void;
}) {
  return (
    <div className="pt-10 text-center">
      <button
        type="button"
        onClick={onBack}
        className="absolute left-4 top-4 inline-flex size-12 items-center justify-center rounded-full text-muted-foreground"
        title="Back to report types"
      >
        <ArrowLeft className="size-6" />
      </button>
      <div className="mx-auto mt-10 flex size-24 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
        <LifeBuoy className="size-12" />
      </div>
      <h2 className="mt-5 text-3xl font-bold">Roadside help</h2>
      <div className="my-8 border-t border-border" />
      <ReportDetailGrid
        details={selected.details}
        selectedDetail={selectedDetail}
        onDetailSelect={onDetailSelect}
      />
      <p className="mx-auto mt-8 max-w-sm text-balance text-base font-semibold text-muted-foreground">
        Having car trouble? Request roadside help and share your location with
        nearby Tazers.
      </p>
      <div className="mt-10 flex gap-3">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="flex-1 rounded-full bg-secondary text-primary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="lg"
          className="flex-1 rounded-full"
          onClick={onSubmit}
        >
          Request
        </Button>
      </div>
    </div>
  );
}
